import omit from 'lodash-es/omit'

import { EMPTY_LIST, EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../constants/file'
import { FILTER_NAME_SUB_MENU } from '../../constants/filter'
import { REQUEST_STATUS } from '../../constants/request'
import { identity } from '../../utils/common'
import { getApiRequestFilterParams } from '../../utils/filter'

import {
  REQUEST_PHASE_PROGRESS_MAX,
  REQUEST_PHASE_PROGRESS_MIN,
  REQUEST_VIEW_FILTERS,
  RequestPhaseItemStatus,
} from './Request.constants'

function getRequestFilterStateFilters({ filters, permanentFilters }) {
  return { ...omit(filters, REQUEST_VIEW_FILTERS), ...permanentFilters }
}

export function isRequestArchived(request) {
  return Boolean(request.archived)
}

export function getRequestTitle({ requestNumber, title }) {
  const hasNumber =
    Number.isSafeInteger(requestNumber) && requestNumber !== null

  if (hasNumber && title) {
    return `${requestNumber} ${title}`
  }

  if (hasNumber) {
    return String(requestNumber)
  }

  return title || EMPTY_STRING
}

export function getRequestTagTooltip({
  archived,
  requestNumber,
  status,
  t,
  title,
}) {
  const mainTitle = getRequestTitle({ requestNumber, title })

  const additionalInfoStatus = Number.isFinite(status)
    ? t(REQUEST_STATUS[status].text)
    : EMPTY_STRING
  const additionalInfoArchived = archived
    ? t('Request.TagTooltipArchivedKey')
    : EMPTY_STRING

  const additionalInfo = [additionalInfoStatus, additionalInfoArchived].filter(
    Boolean
  )

  return additionalInfo.length > 0
    ? `${mainTitle} (${additionalInfo.join(', ')})`
    : mainTitle
}

export function getRequestPhaseStatus(
  progress,
  min = REQUEST_PHASE_PROGRESS_MIN,
  max = REQUEST_PHASE_PROGRESS_MAX
) {
  if (Number.isSafeInteger(progress)) {
    if (progress === min) {
      return RequestPhaseItemStatus.notStarted
    }

    if (progress === max) {
      return RequestPhaseItemStatus.finished
    }

    return RequestPhaseItemStatus.inProgress
  }

  return null
}

export function getRequestPhaseStatusText(status) {
  switch (status) {
    case RequestPhaseItemStatus.finished:
      return 'Request.PhaseFinishedKey'
    case RequestPhaseItemStatus.inProgress:
      return 'Request.PhaseInProgressKey'
    case RequestPhaseItemStatus.notStarted:
      return 'Request.PhaseNotStartedKey'

    default:
      return null
  }
}

export function getTooltipAdditionalText({ selected }) {
  return selected
    ? 'Request.PhaseTooltipSelectedTextKey'
    : 'Request.PhaseTooltipClickToSelectKey'
}

export function toRequestSelectItem(item) {
  return {
    additionalData: item.address_obj?.value,
    isArchived: item.archived,
    label: `${Number.isFinite(item.request_no) ? item.request_no + ' ' : EMPTY_STRING}${item.title}`,
    value: item.id,
  }
}

export function toRequestSelectItems(items) {
  return items.map(toRequestSelectItem)
}

export function toRequestOptionSelectItem(item) {
  return {
    ...toRequestSelectItem(item),
    additionalData: item.building?.title,
  }
}

export function toRequestOptionSelectItems(items) {
  return items.map(toRequestOptionSelectItem)
}

export function toAttachedRequestSelectItem(requestData) {
  return {
    archived: requestData.isArchived,
    id: requestData.id,
    request_no: requestData.requestNumber,
    status: requestData.status?.id,
    title: requestData.title,
  }
}

export function getRequestPermanentFilters({ sub }) {
  return sub ? { [FILTER_NAME_SUB_MENU]: sub } : EMPTY_OBJECT
}

export function parseRequestPermanentFilters(filters) {
  return filters?.[FILTER_NAME_SUB_MENU]
    ? [[FILTER_NAME_SUB_MENU, filters[FILTER_NAME_SUB_MENU]]]
    : EMPTY_LIST
}

export function getRequestFilterStateOptions({
  getPermanentFilters = getRequestPermanentFilters,
  sub,
  transformInitialFilters = identity,
}) {
  function transformFilters(filters) {
    return getRequestFilterStateFilters({
      filters,
      permanentFilters: getPermanentFilters({ sub }),
    })
  }

  function transformLoadedFilters(filters) {
    return transformInitialFilters(transformFilters(filters))
  }

  return {
    transformInitialFilters: transformLoadedFilters,
    transformSaveFilters: transformFilters,
  }
}

export function getRequestApiFilterParams(params) {
  return getApiRequestFilterParams({
    ...params,
    getPermanentFilters: getRequestPermanentFilters,
  })
}

export function getRequestWizardSessionHeader(uuid) {
  return {
    WORKFLOWSESSION: uuid,
  }
}

export function getRequestAttachedFilesProps(requestId) {
  return { scope: FILE_SCOPE_REQUEST, subject: requestId }
}
